import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Select, Button } from "antd";
import {
  ExpandOutlined,
  EditOutlined,
  ShareAltOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useTags } from "hooks";
import RichTextEditor from "components/common/RichTextEditor";

const ProjectFormDrawer = ({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialValues = null,
  loading,
  onTagsUpdate,
}) => {
  const { tags } = useTags();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localTags, setLocalTags] = useState(initialValues?.tags || []);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";

      return () => {
        // Restore scroll position
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.overflow = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  useEffect(() => {
    if (mode === "edit" && initialValues) {
      form.setFieldsValue(initialValues);
      setLocalTags(initialValues.tags || []);
    } else {
      form.resetFields();
    }
  }, [open, initialValues, mode, form]);

  const handleFinish = (values) => {
    onSubmit(values);
    if (mode === "create") {
      form.resetFields();
    }
  };

  const handleClose = () => {
    form.resetFields();
    setLocalTags([]);
    onClose?.();
  };

  return (
    <Drawer
      zIndex={99999}
      placement="right"
      closable={false}
      size={isExpanded ? 520 : "100vw"}
      onClose={handleClose}
      open={open}
      destroyOnHidden
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: isExpanded ? "100vw" : 700,
            margin: isExpanded ? 0 : "0 auto",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {mode === "edit" ? "Save Changes" : "Create"}
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 700,
          margin: isExpanded ? 0 : "0 auto",
        }}
      >
        {/* Breadcrumb / page context */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 15, color: "#6b7280", fontFamily: "Inter" }}>
            <span style={{ cursor: "pointer" }}>Project</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {mode === "edit" ? "Edit Project" : "Create Project"}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            {/* <ShareAltOutlined className="task-drawer-icon" /> */}
            <MoreOutlined className="task-drawer-icon" />
          </div>
        </div>

        <Form
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          initialValues={initialValues}
        >
          {/* Code */}
          <Form.Item
            label="Project Code"
            name="code"
            rules={[{ required: true, message: "Project code is required" }]}
          >
            <Input
              placeholder="e.g. PAYMENTS, OLITASK, UG"
              maxLength={50}
              disabled={mode === "edit"}
            />
          </Form.Item>

          {/* Name */}
          <Form.Item
            label="Project Name"
            name="name"
            rules={[{ required: true, message: "Project name is required" }]}
          >
            <Input placeholder="Enter project name" maxLength={255} />
          </Form.Item>

          {mode === "create" && (
            <Form.Item
              name="targetPoints"
              label="Target Points"
              rules={[{ required: true, message: "Target points is required" }]}
            >
              <Input placeholder="Enter estimate points" />
            </Form.Item>
          )}

          {/* Owner */}
          <Form.Item
            label="Owner Name"
            name="ownerName"
            rules={[{ required: true, message: "Owner name is required" }]}
          >
            <Input placeholder="e.g. Alice Johnson" />
          </Form.Item>

          {/* Tags */}
          {mode === "create" && (
            <Form.Item label="Tags" name="tags">
              <Select
                mode="multiple"
                placeholder="Select or create tags"
                maxTagCount={isExpanded ? 7 : 4}
                options={tags.map((tag) => ({
                  value: tag.id,
                  label: tag.name,
                }))}
              />
            </Form.Item>
          )}

          {/* Description */}
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Description is required" }]}
            style={{ marginBottom: 10 }}
          >
            <RichTextEditor
              height="150px"
              placeholder="Write project description..."
            />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
};

export default ProjectFormDrawer;
